import { SearchProvider, SearchProviderType } from '@/types/searchProvider';
import { SolutionCardProps } from '@/components/SolutionCard';
import { supabase } from '@/integrations/supabase/client';

export class StandardSearchProvider implements SearchProvider {
  getProviderType(): SearchProviderType {
    return 'search-standard';
  }
  
  getExpectedResponseTime(): number {
    return 500; // 500ms expected response time
  }
  
  isNaturalLanguageSupported(): boolean {
    return false; // Standard search uses pattern matching
  }
  
  getDisplayName(): string {
    return 'Standard Search';
  }
  
  async search(query: string): Promise<SolutionCardProps[]> {
    if (!query || query.trim().length === 0) {
      return [];
    }
    
    const startTime = performance.now();
    
    try {
      // Perform search using Supabase's text search (exclude classified)
      const { data, error } = await supabase
        .from('solutions')
        .select(`
          id, 
          name, 
          short_description, 
          featured, 
          image_url,
          slug,
          solution_categories(categories(name)),
          solution_tags(tags(name))
        `)
        .or('status.is.null,status.neq.classified')
        .or(`name.ilike.%${query}%,short_description.ilike.%${query}%`);
        
      if (error) {
        console.error('Standard search error:', error);
        throw error;
      }
      
      // Transform data to match SolutionCardProps
      const solutions = data.map(solution => {
        // Extract category from solution_categories if available
        const category = solution.solution_categories?.[0]?.categories?.name || 'Uncategorized';
        
        // Extract tags from solution_tags if available
        const tags = solution.solution_tags
          ?.map(tag => tag.tags?.name)
          .filter(Boolean) || [];
          
        return {
          id: solution.id,
          name: solution.name,
          category,
          shortDescription: solution.short_description,
          tags,
          featured: solution.featured,
          image: solution.image_url,
          slug: solution.slug
        };
      });
      
      const endTime = performance.now();
      const responseTime = endTime - startTime;
      
      console.log(`Standard search completed in ${responseTime.toFixed(2)}ms for query: "${query}"`);
      
      return solutions;
      
    } catch (error) {
      console.error('Standard search provider error:', error);
      throw new Error(`Standard search failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
} 